import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Button, CheckBox } from "components/atoms";
import {
  DocumentUploadModal,
  GeneralSelectInput,
  TextInput,
  MessageTextAreaInput,
  DatePickerInput,
  RadioButtonGroup,
  TabbedFormToggle,
} from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { documentService } from "services";
import {
  ApplicationFormValues,
  gradeOptions,
  countriesFlat,
  FileWithType,
} from "types";

interface AppAcademicSectionProps {
  currentSubStage?: string;
  destinationCountry?: string;
}

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set([
    "personalInformation",
    "englishLanguageTestScores",
    "highSchool",
    "tertiaryEducation1",
    "tertiaryEducation2",
    "tertiaryEducation3",
    "educationDetails",
    "scholarshipFinancialSupport",
    "healthInsurance",
    "studentSupportServices",
    "employment1",
    "employment2",
    "employment3",
    "employmentDetails",
  ]),
};

const AppAcademicSection: React.FC<AppAcademicSectionProps> = ({
  currentSubStage = "edOverview",
  destinationCountry = "Australia",
}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ApplicationFormValues>();

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    highSchoolCertificate?: { id: string; fileName: string };
    highSchoolTranscript?: { id: string; fileName: string };
    tertiaryCertificate1?: { id: string; fileName: string };
    tertiaryCertificate2?: { id: string; fileName: string };
    tertiaryCertificate3?: { id: string; fileName: string };
    tertiaryTranscript1?: { id: string; fileName: string };
    tertiaryTranscript2?: { id: string; fileName: string };
    tertiaryTranscript3?: { id: string; fileName: string };
    creditTransfer?: { id: string; fileName: string };
  }>({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024, // 5MB
      enableMagicNumberValidation: false, // Disabled for sync validation
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Documents uploaded successfully.",
  });

  const academicTypes = [
    "HIGH_SCHOOL_CERTIFICATE",
    "HIGH_SCHOOL_TRANSCRIPT",
    "TERTIARY_CERTIFICATE_1",
    "TERTIARY_CERTIFICATE_2",
    "TERTIARY_CERTIFICATE_3",
    "TERTIARY_TRANSCRIPT_1",
    "TERTIARY_TRANSCRIPT_2",
    "TERTIARY_TRANSCRIPT_3",
    "CREDIT_TRANSFER",
  ];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(academicTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const getDocumentFields = (filterByStage?: string) => {
    const allDocuments = [
      {
        id: "credittransfer",
        label: "Course Outline",
        description:
          "Combined course outlines for all courses (can be multiple files in one PDF)",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!values.isApplyingForCreditTransfer,
        uploaded: !!uploadedDocuments.creditTransfer,
        stage: "edOverview",
      },
      {
        id: "highschoolcertificate",
        label: "High School Certificate",
        description: "Clear copy of high school certificate",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: true,
        uploaded: !!uploadedDocuments.highSchoolCertificate,
        stage: "highschool",
      },
      {
        id: "highschooltranscript",
        label: "High School Transcript",
        description: "Clear copy of high school transcript",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: true,
        uploaded: !!uploadedDocuments.highSchoolTranscript,
        stage: "highschool",
      },
      {
        id: "tertiarycertificate1",
        label: "Tertiary Certificate",
        description: "Clear copy of tertiary certificate",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: true,
        uploaded: !!uploadedDocuments.tertiaryCertificate1,
        stage: "tertiary1",
      },
      {
        id: "tertiarytranscript1",
        label: "Tertiary Transcript",
        description: "Clear copy of tertiary transcript",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: true,
        uploaded: !!uploadedDocuments.tertiaryTranscript1,
        stage: "tertiary1",
      },
      {
        id: "tertiarycertificate2",
        label: "2nd Tertiary Certificate",
        description: "Clear copy of 2nd tertiary certificate",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!isTertiaryFieldsStarted(2),
        uploaded: !!uploadedDocuments.tertiaryCertificate2,
        stage: "tertiary2",
      },
      {
        id: "tertiarytranscript2",
        label: "2nd Tertiary Transcript",
        description: "Clear copy of 2nd tertiary transcript",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!isTertiaryFieldsStarted(2),
        uploaded: !!uploadedDocuments.tertiaryTranscript2,
        stage: "tertiary2",
      },
      {
        id: "tertiarycertificate3",
        label: "3rd Tertiary Certificate",
        description: "Clear copy of 3rd tertiary certificate",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!isTertiaryFieldsStarted(3),
        uploaded: !!uploadedDocuments.tertiaryCertificate3,
        stage: "tertiary3",
      },
      {
        id: "tertiarytranscript3",
        label: "3rd Tertiary Transcript",
        description: "Clear copy of 3rd tertiary transcript",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!isTertiaryFieldsStarted(3),
        uploaded: !!uploadedDocuments.tertiaryTranscript3,
        stage: "tertiary3",
      },
    ];

    if (filterByStage) {
      return allDocuments.filter(
        (doc) => doc.stage === filterByStage && doc.required,
      );
    }

    return allDocuments;
  };

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs =
            await documentService.fetchDocumentsWithMapping(academicTypes);

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  const isTertiaryFieldsStarted = (level: number) => {
    const prefix = `tertiary${level}`;

    const hasAnyValue = [
      values[`${prefix}School` as keyof ApplicationFormValues],
      values[`${prefix}Course` as keyof ApplicationFormValues],
      values[`${prefix}City` as keyof ApplicationFormValues],
      values[`${prefix}Country` as keyof ApplicationFormValues],
      values[`${prefix}GradeObtained` as keyof ApplicationFormValues],
      values[`${prefix}StartDate` as keyof ApplicationFormValues],
      values[`${prefix}EndDate` as keyof ApplicationFormValues],
    ].some((val) => {
      if (val instanceof Date) return true;
      return val && val !== "";
    });

    const tertiaryData =
      level === 1
        ? userData?.tertiaryEducation1
        : level === 2
          ? userData?.tertiaryEducation2
          : userData?.tertiaryEducation3;

    const hasExistingData =
      tertiaryData &&
      (tertiaryData.school ||
        tertiaryData.course ||
        tertiaryData.city ||
        tertiaryData.country ||
        tertiaryData.gradeObtained ||
        tertiaryData.startDate ||
        tertiaryData.endDate);

    return hasAnyValue || hasExistingData;
  };

  const getFieldError = (fieldName: string): string | undefined => {
    const error = errors[fieldName as keyof ApplicationFormValues];
    return typeof error === "string" ? error : undefined;
  };

  const getFieldTouched = (fieldName: string): boolean | undefined => {
    const touch = touched[fieldName as keyof ApplicationFormValues];
    return typeof touch === "boolean" ? touch : undefined;
  };

  const getCreditError = (courseIndex: number, field: string) => {
    const error = errors.creditTransfer?.[courseIndex];
    return typeof error === "object"
      ? error?.[field as keyof typeof error]
      : undefined;
  };

  const getCreditTouched = (courseIndex: number, field: string) => {
    const touch = touched.creditTransfer?.[courseIndex];
    return typeof touch === "object"
      ? touch?.[field as keyof typeof touch]
      : undefined;
  };

  const handleCurrentlyStudyingChange = async (
    field: string,
    checked: boolean,
  ) => {
    if (checked) {
      if (field === "highSchoolIsStillAttending") {
        setFieldValue("highSchoolIsStillAttending", true);
        setFieldValue("tertiary1IsStillAttending", false);
        setFieldValue("highSchoolEndDate", null);
      } else if (field === "tertiary1IsStillAttending") {
        setFieldValue("highSchoolIsStillAttending", false);
        setFieldValue("tertiary1IsStillAttending", true);
        setFieldValue("tertiary1EndDate", null);
      }
    } else {
      setFieldValue(field, false);
    }
  };

  const isHighSchoolComplete = () => {
    return !!(
      values.highSchoolName &&
      values.highSchoolCity &&
      values.highSchoolCountry &&
      values.highSchoolGradeObtained &&
      values.highSchoolStartDate &&
      values.highSchoolEndDate
    );
  };

  const isTertiaryComplete = (level: number) => {
    const prefix = `tertiary${level}`;
    return !!(
      values[`${prefix}School` as keyof ApplicationFormValues] &&
      values[`${prefix}Course` as keyof ApplicationFormValues] &&
      values[`${prefix}City` as keyof ApplicationFormValues] &&
      values[`${prefix}Country` as keyof ApplicationFormValues] &&
      values[`${prefix}GradeObtained` as keyof ApplicationFormValues] &&
      values[`${prefix}StartDate` as keyof ApplicationFormValues] &&
      values[`${prefix}EndDate` as keyof ApplicationFormValues]
    );
  };

  const getInstitutionOptions = () => {
    const institutions = [];
    if (values.tertiary1School)
      institutions.push({
        value: values.tertiary1School,
        label: values.tertiary1School,
      });
    if (values.tertiary2School)
      institutions.push({
        value: values.tertiary2School,
        label: values.tertiary2School,
      });
    institutions.push({ value: "other", label: "Other" });
    return institutions;
  };

  const renderCreditTransferForm = (courseIndex: number) => (
    <div className="credit-transfer-entry">
      <GeneralSelectInput
        id={`creditTransfer[${courseIndex}].institutionName`}
        label="Institution Name"
        options={getInstitutionOptions()}
        value={values.creditTransfer?.[courseIndex]?.institutionName || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        error={getCreditError(courseIndex, "institutionName")}
        touched={getCreditTouched(courseIndex, "institutionName")}
      />
      {values.creditTransfer?.[courseIndex]?.institutionName === "other" && (
        <TextInput
          id={`creditTransfer[${courseIndex}].institutionName`}
          label="Enter Institution Name"
          placeholder="Enter institution name"
          getFieldProps={() => ({
            value: "",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(
                `creditTransfer[${courseIndex}].institutionName`,
                e.target.value,
              );
            },
            onBlur: handleBlur,
          })}
        />
      )}
      <TextInput
        id={`creditTransfer[${courseIndex}].courseCode`}
        label="Course Code"
        placeholder="e.g., BUS101"
        getFieldProps={() => ({
          value: values.creditTransfer?.[courseIndex]?.courseCode || "",
          onChange: handleChange,
          onBlur: handleBlur,
        })}
        error={getCreditError(courseIndex, "courseCode")}
        touched={getCreditTouched(courseIndex, "courseCode")}
      />
      <TextInput
        id={`creditTransfer[${courseIndex}].courseName`}
        label="Course Name"
        placeholder="e.g., Introduction to Business"
        getFieldProps={() => ({
          value: values.creditTransfer?.[courseIndex]?.courseName || "",
          onChange: handleChange,
          onBlur: handleBlur,
        })}
        error={getCreditError(courseIndex, "courseName")}
        touched={getCreditTouched(courseIndex, "courseName")}
      />
      <div className="credit-completion-date-container">
        <DatePickerInput
          id={`creditTransfer[${courseIndex}].dateCompleted`}
          className={`credit-transfer-date-${courseIndex}`}
          label="Date Completed"
          value={values.creditTransfer?.[courseIndex]?.dateCompleted || null}
          onChange={(date) =>
            setFieldValue(`creditTransfer[${courseIndex}].dateCompleted`, date)
          }
          preset="pastDate"
        />
      </div>
    </div>
  );

  const renderEducationOverviewSection = () => (
    <div className="education-overview-section">
      <div className="form-section">
        <div className="form-group">
          <h4>Education Overview & Credit Transfer</h4>
        </div>

        <div className="form-fields">
          <RadioButtonGroup
            name="completedYear12InAustralia"
            label={`Did you complete year 12 in ${destinationCountry}? *`}
            value={values.completedYear12InAustralia}
            onChange={(value) => {
              setFieldValue("completedYear12InAustralia", value);
              setFieldTouched("completedYear12InAustralia", true);
            }}
          />
          {destinationCountry === "Australia" && (
            <>
              {values.completedYear12InAustralia && (
                <TextInput
                  id="atarScore"
                  label="University Admission Score (ATAR/UAI/OP) *"
                  placeholder="Enter university admission score"
                  getFieldProps={() => ({
                    value: values.atarScore,
                    onChange: handleChange,
                    onBlur: handleBlur,
                  })}
                  error={errors.atarScore}
                  touched={touched.atarScore}
                />
              )}
              <TextInput
                id="usiNumber"
                label="USI Number"
                placeholder="Enter your Unique Student Identifier"
                getFieldProps={() => ({
                  value: values.usiNumber,
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.usiNumber}
                touched={touched.usiNumber}
              />
            </>
          )}
          <RadioButtonGroup
            name="hasBeenExcluded"
            label="Excluded from a course/program and/or institution *"
            value={values.hasBeenExcluded}
            onChange={(value) => {
              setFieldValue("hasBeenExcluded", value);
              setFieldTouched("hasBeenExcluded", true);
            }}
          />
          {values.hasBeenExcluded && (
            <MessageTextAreaInput
              id="exclusionDetails"
              label="Exclusion Details *"
              placeholder="Please provide details about the exclusion (e.g., reason for exclusion, institution name, dates, circumstances)"
              rows="4"
              maxLength={500}
              getFieldProps={() => ({
                value: values.exclusionDetails,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={errors.exclusionDetails}
              touched={touched.exclusionDetails}
            />
          )}

          <RadioButtonGroup
            name="isApplyingForCreditTransfer"
            label="Are you applying for credit transfer? *"
            value={values.isApplyingForCreditTransfer}
            onChange={(value) => {
              setFieldValue("isApplyingForCreditTransfer", value);
              setFieldTouched("isApplyingForCreditTransfer", true);
            }}
          />

          {values.isApplyingForCreditTransfer && (
            <>
              <TabbedFormToggle
                displayTabs={[
                  {
                    id: "creditTransfer0",
                    label: "Course 1",
                    component: renderCreditTransferForm(0),
                  },
                  ...(values.creditTransfer?.[0]?.institutionName ||
                  values.creditTransfer?.[0]?.courseCode ||
                  values.creditTransfer?.[0]?.courseName
                    ? [
                        {
                          id: "creditTransfer1",
                          label: "Course 2",
                          component: renderCreditTransferForm(1),
                        },
                      ]
                    : []),
                  ...(values.creditTransfer?.[1]?.institutionName ||
                  values.creditTransfer?.[1]?.courseCode ||
                  values.creditTransfer?.[1]?.courseName
                    ? [
                        {
                          id: "creditTransfer2",
                          label: "Course 3",
                          component: renderCreditTransferForm(2),
                        },
                      ]
                    : []),
                  ...(values.creditTransfer?.[2]?.institutionName ||
                  values.creditTransfer?.[2]?.courseCode ||
                  values.creditTransfer?.[2]?.courseName
                    ? [
                        {
                          id: "creditTransfer3",
                          label: "Course 4",
                          component: renderCreditTransferForm(3),
                        },
                      ]
                    : []),
                  ...(values.creditTransfer?.[3]?.institutionName ||
                  values.creditTransfer?.[3]?.courseCode ||
                  values.creditTransfer?.[3]?.courseName
                    ? [
                        {
                          id: "creditTransfer4",
                          label: "Course 5",
                          component: renderCreditTransferForm(4),
                        },
                      ]
                    : []),
                  ...(values.creditTransfer?.[4]?.institutionName ||
                  values.creditTransfer?.[4]?.courseCode ||
                  values.creditTransfer?.[4]?.courseName
                    ? [
                        {
                          id: "creditTransfer5",
                          label: "Course 6",
                          component: renderCreditTransferForm(5),
                        },
                      ]
                    : []),
                  ...(values.creditTransfer?.[5]?.institutionName ||
                  values.creditTransfer?.[5]?.courseCode ||
                  values.creditTransfer?.[5]?.courseName
                    ? [
                        {
                          id: "creditTransfer6",
                          label: "Course 7",
                          component: renderCreditTransferForm(6),
                        },
                      ]
                    : []),
                ]}
                initialTab="creditTransfer0"
                isSubStageComplete={(tabId) => {
                  const index = parseInt(tabId.replace("creditTransfer", ""));
                  const course = values.creditTransfer?.[index];
                  return !!(
                    course?.institutionName &&
                    course?.courseCode &&
                    course?.courseName &&
                    course?.dateCompleted
                  );
                }}
              />

              {documentService.countRequiredDocs(
                getDocumentFields(currentSubStage),
              ) > 0 && (
                <div className="documents-section">
                  <div className="upload-header">
                    <Button
                      type="button"
                      text="Upload Documents"
                      onClick={() => setShowDocumentModal(true)}
                      variant="border"
                      width="10rem"
                    />
                    <div className="docs-counter">
                      <span className="uploaded-count">
                        {documentService.countUploadedDocs(
                          getDocumentFields(currentSubStage),
                        )}
                      </span>
                      <span className="separator">/</span>
                      <span className="total-count">
                        {documentService.countRequiredDocs(
                          getDocumentFields(currentSubStage),
                        )}
                      </span>
                      <span className="docs-label">documents</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Course Syllabi"
        description="Please attach course syllabi/outlines for credit transfer assessment:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderHighSchoolSection = () => (
    <div className="high-school-section">
      <div className="form-section">
        <div className="form-group">
          <h4>High School</h4>
          <p>Enter your high school information.</p>
        </div>

        <div className="form-fields">
          <TextInput
            id="highSchoolName"
            label="School Name *"
            placeholder="Enter school name"
            getFieldProps={() => ({
              value: values.highSchoolName,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.highSchoolName}
            touched={touched.highSchoolName}
          />
          <TextInput
            id="highSchoolCity"
            label="City *"
            placeholder="Enter city"
            getFieldProps={() => ({
              value: values.highSchoolCity,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.highSchoolCity}
            touched={touched.highSchoolCity}
          />
          <GeneralSelectInput
            id="highSchoolCountry"
            label="Country *"
            options={countriesFlat}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.highSchoolCountry}
            error={errors.highSchoolCountry}
            touched={touched.highSchoolCountry}
          />
          <GeneralSelectInput
            id="highSchoolGradeObtained"
            label="Grade *"
            options={gradeOptions}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.highSchoolGradeObtained}
            error={errors.highSchoolGradeObtained}
            touched={touched.highSchoolGradeObtained}
          />

          <div className="high-date-container">
            <DatePickerInput
              id="highSchoolStartDate"
              className="highSchoolStartDate"
              label="Start Date"
              value={values.highSchoolStartDate as Date | null}
              onChange={(date) => setFieldValue("highSchoolStartDate", date)}
              preset="pastDate"
            />
            <DatePickerInput
              id="highSchoolEndDate"
              className="highSchoolEndDate"
              label="End Date"
              value={values.highSchoolEndDate as Date | null}
              disabled={values.highSchoolIsStillAttending as boolean}
              onChange={(date) => setFieldValue("highSchoolEndDate", date)}
              preset="pastDate"
            />
          </div>

          <CheckBox
            label="Currently studying"
            checked={values.highSchoolIsStillAttending as boolean}
            disabled={
              !isHighSchoolComplete() || !!values.tertiary1IsStillAttending
            }
            onChange={(checked) =>
              handleCurrentlyStudyingChange(
                "highSchoolIsStillAttending",
                checked,
              )
            }
          />

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload High School Documents"
        description="Please attach a copy of your high school certificate:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderTertiaryEducationSection = (index: number) => {
    const prefix = `tertiary${index}`;
    const isRequired = index === 1;
    const labels = ["Latest", "Second", "Third"];
    const descriptions = [
      "Enter your most recent tertiary education.",
      "Enter your second tertiary education (optional).",
      "Enter your third tertiary education (optional).",
    ];

    return (
      <div className="tertiary-education-section">
        <div className="form-section">
          <div className="form-group">
            <h4>{labels[index - 1]} Tertiary Education</h4>
            <p>{descriptions[index - 1]}</p>
          </div>

          <div className="form-fields">
            <TextInput
              id={`${prefix}School`}
              label={`Institution Name ${isRequired ? "*" : ""}`}
              placeholder="Enter institution name"
              getFieldProps={() => ({
                value: values[
                  `${prefix}School` as keyof ApplicationFormValues
                ] as string,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={getFieldError(`${prefix}School`)}
              touched={getFieldTouched(`${prefix}School`)}
            />
            <TextInput
              id={`${prefix}Course`}
              label={`Name of Course ${isRequired ? "*" : ""}`}
              placeholder="Enter name of course"
              getFieldProps={() => ({
                value: values[
                  `${prefix}Course` as keyof ApplicationFormValues
                ] as string,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={getFieldError(`${prefix}Course`)}
              touched={getFieldTouched(`${prefix}Course`)}
            />
            <TextInput
              id={`${prefix}City`}
              label={`City ${isRequired ? "*" : ""}`}
              placeholder="Enter city"
              getFieldProps={() => ({
                value: values[
                  `${prefix}City` as keyof ApplicationFormValues
                ] as string,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={getFieldError(`${prefix}City`)}
              touched={getFieldTouched(`${prefix}City`)}
            />
            <GeneralSelectInput
              id={`${prefix}Country`}
              label={`Country ${isRequired ? "*" : ""}`}
              options={countriesFlat}
              onChange={handleChange}
              onBlur={handleBlur}
              value={
                values[
                  `${prefix}Country` as keyof ApplicationFormValues
                ] as string
              }
              error={getFieldError(`${prefix}Country`)}
              touched={getFieldTouched(`${prefix}Country`)}
            />
            <GeneralSelectInput
              id={`${prefix}GradeObtained`}
              label={`Grade ${isRequired ? "*" : ""}`}
              options={gradeOptions}
              onChange={handleChange}
              onBlur={handleBlur}
              value={
                values[
                  `${prefix}GradeObtained` as keyof ApplicationFormValues
                ] as string
              }
              error={getFieldError(`${prefix}GradeObtained`)}
              touched={getFieldTouched(`${prefix}GradeObtained`)}
            />

            <div className="tertiary-date-container">
              <DatePickerInput
                id={`${prefix}StartDate`}
                className={`${prefix}StartDate`}
                label="Start Date"
                value={
                  values[
                    `${prefix}StartDate` as keyof ApplicationFormValues
                  ] as Date | null
                }
                onChange={(date) => setFieldValue(`${prefix}StartDate`, date)}
                preset="pastDate"
              />

              <DatePickerInput
                id={`${prefix}EndDate`}
                className={`${prefix}EndDate`}
                label="End Date"
                value={
                  values[
                    `${prefix}EndDate` as keyof ApplicationFormValues
                  ] as Date | null
                }
                disabled={
                  values[
                    `${prefix}IsStillAttending` as keyof ApplicationFormValues
                  ] as boolean
                }
                onChange={(date) => setFieldValue(`${prefix}EndDate`, date)}
                preset="pastDate"
              />
            </div>
            {index === 1 && (
              <CheckBox
                label="Currently studying"
                checked={
                  values[
                    `${prefix}IsStillAttending` as keyof ApplicationFormValues
                  ] as boolean
                }
                disabled={
                  !isTertiaryComplete(index) ||
                  !!values.highSchoolIsStillAttending
                }
                onChange={(checked) =>
                  handleCurrentlyStudyingChange(
                    `${prefix}IsStillAttending`,
                    checked,
                  )
                }
              />
            )}

            {documentService.countRequiredDocs(
              getDocumentFields(currentSubStage),
            ) > 0 && (
              <div className="documents-section">
                <div className="upload-header">
                  <Button
                    type="button"
                    text="Upload Documents"
                    onClick={() => setShowDocumentModal(true)}
                    variant="border"
                    width="10rem"
                  />
                  <div className="docs-counter">
                    <span className="uploaded-count">
                      {documentService.countUploadedDocs(
                        getDocumentFields(currentSubStage),
                      )}
                    </span>
                    <span className="separator">/</span>
                    <span className="total-count">
                      {documentService.countRequiredDocs(
                        getDocumentFields(currentSubStage),
                      )}
                    </span>
                    <span className="docs-label">documents</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DocumentUploadModal
          isOpen={showDocumentModal}
          onClose={() => {
            handleError("");
            setShowDocumentModal(false);
          }}
          title={`Upload Academic Documents`}
          description="Please attach copies of your certificate and transcript:"
          documents={getDocumentFields(currentSubStage)}
          onFileUpload={handleUploadWithRefresh}
          onFileRemove={handleFileRemove}
          validateFile={validateFileSync}
          acceptedFileTypes={getAcceptedFileTypes()}
          onError={handleError}
          errorMessage={errorMessage}
          successMessage={successMessage}
          maxFileSize={getMaxFileSize()}
          stage={stage}
          uploadProgress={uploadProgress}
        />
      </div>
    );
  };

  switch (currentSubStage) {
    case "edOverview":
      return renderEducationOverviewSection();
    case "highschool":
      return renderHighSchoolSection();
    case "tertiary1":
      return renderTertiaryEducationSection(1);
    case "tertiary2":
      return renderTertiaryEducationSection(2);
    case "tertiary3":
      return renderTertiaryEducationSection(3);
    default:
      return renderEducationOverviewSection();
  }
};

export default AppAcademicSection;
